/**
 * Finding Your Voice — Achievements & Levels
 *
 * SINGLE SOURCE OF TRUTH for gamification. Consumed by:
 *  - GamificationContext (levels, achievement checks, XP awards on unlock)
 *  - AchievementsPage (display grid)
 *
 * Each achievement carries a `check(context)` predicate. The context is
 * assembled by GamificationContext.checkAchievements from its own state
 * (daysVisited, levelNumber) merged with the progress snapshot passed by
 * the caller (see ProgressContext.getAchievementContext).
 *
 * Design rule (handoff "Progress vs. Pressure" + fleet anti-streak stance):
 * return badges are CUMULATIVE days visited, never consecutive-day streaks —
 * a reset-to-zero counter punishes exactly the anxious learners this app
 * serves. The streak stat may exist elsewhere; badges never depend on it.
 */

export const LEVELS = [
  { number: 1, name: 'Newcomer', xpRequired: 0, description: 'You have entered the courtyard.' },
  { number: 2, name: 'Listener', xpRequired: 100, description: 'You are learning to hear: yourself, your guides, your audience.' },
  { number: 3, name: 'Speaker', xpRequired: 300, description: 'You have found words worth saying and the courage to say them.' },
  { number: 4, name: 'Storyteller', xpRequired: 600, description: 'Your words carry weight because they carry truth.' },
  { number: 5, name: 'Orator', xpRequired: 1000, description: 'You speak with clarity, presence, and connection.' },
  { number: 6, name: 'Voice Found', xpRequired: 1500, description: 'Your voice is your own. The courtyard is proud.' },
];

export const ACHIEVEMENTS = [
  // --- Module milestones ---
  {
    id: 'first_module',
    name: 'First Steps',
    description: 'Finish your first evening.',
    emoji: '👣',
    category: 'modules',
    xpReward: 25,
    check: (c) => (c.modulesCompleted || 0) >= 1,
  },
  {
    id: 'half_way',
    name: 'Halfway There',
    description: 'Finish four evenings. Half of them are behind you.',
    emoji: '🌗',
    category: 'modules',
    xpReward: 50,
    check: (c) => (c.modulesCompleted || 0) >= 4,
  },
  {
    id: 'all_modules',
    name: 'All Eight Evenings',
    description: 'Finish all eight evenings. The courtyard stands and applauds.',
    emoji: '🏆',
    category: 'modules',
    xpReward: 150,
    check: (c) => (c.modulesCompleted || 0) >= 8,
  },

  // --- Guide devotions ---
  {
    id: 'philemon_devotee',
    name: "Philemon's Student",
    description: "Finish Philemon's evenings: 1, 2 and 5.",
    emoji: '💚',
    category: 'characters',
    xpReward: 50,
    check: (c) => [1, 2, 5].every((m) => (c.completedIds || []).includes(m)),
  },
  {
    id: 'theron_devotee',
    name: "Theron's Apprentice",
    description: "Finish Theron's evenings: 3, 6 and 7.",
    emoji: '🧭',
    category: 'characters',
    xpReward: 50,
    check: (c) => [3, 6, 7].every((m) => (c.completedIds || []).includes(m)),
  },
  {
    id: 'xenia_devotee',
    name: "Xenia's Guest of Honour",
    description: "Finish Xenia's evenings: 4 and 8.",
    emoji: '🏺',
    category: 'characters',
    xpReward: 50,
    check: (c) => [4, 8].every((m) => (c.completedIds || []).includes(m)),
  },

  // --- Assessment ---
  {
    id: 'assessed',
    name: 'Self-Knowledge',
    description: 'Complete the speaking confidence assessment.',
    emoji: '🎯',
    category: 'assessment',
    xpReward: 30,
    check: (c) => !!c.assessmentDone || (c.assessmentCount || 0) >= 1,
  },
  {
    id: 'reassessed',
    name: 'A Second Look',
    description: 'Sit down for the assessment a second time. Your answers may have changed.',
    emoji: '📈',
    category: 'assessment',
    xpReward: 40,
    check: (c) => (c.assessmentCount || 0) >= 2,
  },

  // --- Toolkit ---
  {
    id: 'first_tool',
    name: 'Tool in Hand',
    description: 'Use your first toolkit tool.',
    emoji: '🛠️',
    category: 'toolkit',
    xpReward: 15,
    check: (c) => (c.toolsUsed || 0) >= 1,
  },
  {
    id: 'toolkit_explorer',
    name: 'Toolkit Explorer',
    description: 'Use five different tools from the toolkit.',
    emoji: '🧰',
    category: 'toolkit',
    xpReward: 40,
    check: (c) => (c.toolsUsed || 0) >= 5,
  },
  {
    id: 'toolkit_master',
    name: 'Toolkit Master',
    description: 'Use every tool in the toolkit at least once.',
    emoji: '⭐',
    category: 'toolkit',
    xpReward: 75,
    check: (c) => (c.toolsUsed || 0) >= 10,
  },

  // --- Returning to the courtyard (cumulative days, never streaks) ---
  {
    id: 'three_evenings',
    name: 'A Familiar Face',
    description: 'Return to the courtyard on three separate days.',
    emoji: '🌿',
    category: 'returns',
    xpReward: 20,
    check: (c) => (c.daysVisited || 0) >= 3,
  },
  {
    id: 'ten_evenings',
    name: 'A Regular at the Courtyard',
    description: 'Ten separate days in the courtyard, whenever they happened.',
    emoji: '🍇',
    category: 'returns',
    xpReward: 50,
    check: (c) => (c.daysVisited || 0) >= 10,
  },
  {
    id: 'thirty_evenings',
    name: 'The Devoted Student',
    description: 'Thirty separate days. The guides know your cushion by name.',
    emoji: '🏛️',
    category: 'returns',
    xpReward: 100,
    check: (c) => (c.daysVisited || 0) >= 30,
  },

  // --- Special ---
  {
    id: 'scene_setter_complete',
    name: 'Welcome to the Courtyard',
    description: 'Read the story of how the courtyard came to be.',
    emoji: '🚪',
    category: 'special',
    xpReward: 20,
    check: (c) => !!c.hasSeenSceneSetter,
  },
  {
    id: 'resources_explorer',
    name: 'The Curious Reader',
    description: 'Browse the recommended resources library.',
    emoji: '📚',
    category: 'special',
    xpReward: 15,
    check: (c) => !!c.resourcesVisited,
  },
  {
    id: 'voice_found',
    name: 'Voice Found',
    description: 'Reach the highest level. Your voice is your own.',
    emoji: '🎤',
    category: 'special',
    xpReward: 0,
    check: (c) => (c.levelNumber || 1) >= 6,
  },
];

/**
 * Returns achievements newly satisfied by `context` that aren't in `unlocked`.
 * Pure — no side effects; GamificationContext applies state/XP.
 */
export const findNewlyUnlocked = (context, unlocked = []) =>
  ACHIEVEMENTS.filter((a) => !unlocked.includes(a.id) && a.check(context));
